import { useState, useMemo } from 'react';
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  InputAdornment,
  IconButton,
  Tooltip,
  Paper,
  Typography,
  Menu,
  MenuItem,
  useTheme,
} from '@mui/material';
import {
  Search as SearchIcon,
  Refresh as RefreshIcon,
  FileDownload as DownloadIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ArrowUpward as ArrowUpwardIcon,
  ArrowDownward as ArrowDownwardIcon,
} from '@mui/icons-material';
import SkeletonTable from './SkeletonLoaders';
import { downloadCSV, downloadJSON, prepareDataForExport } from '../../utils/exportUtils';
import { en } from '../../locale';

export interface Column<T> {
  key: keyof T;
  label: string;
  render?: (value: T[keyof T], row: T) => React.ReactNode;
  sortable?: boolean;
  width?: string | number;
  align?: 'left' | 'center' | 'right';
}

export interface DataTableProps<T extends { id: number }> {
  data: T[];
  columns: Column<T>[];
  isLoading?: boolean;
  searchable?: boolean;
  searchPlaceholder?: string;
  exportable?: boolean;
  exportFilename?: string;
  onRefresh?: () => void;
  onEdit?: (row: T) => void;
  onDelete?: (row: T) => void;
  actions?: (row: T) => React.ReactNode;
  emptyMessage?: string;
  emptyIcon?: React.ReactNode;
  rowsPerPageOptions?: number[];
  dense?: boolean;
}

/**
 * Composant de table de données générique et réutilisable
 * Supporte la recherche, le tri, la pagination et l'export
 */
export default function DataTable<T extends { id: number }>({
  data,
  columns,
  isLoading = false,
  searchable = true,
  searchPlaceholder = en.common.search,
  exportable = true,
  exportFilename = 'export',
  onRefresh,
  onEdit,
  onDelete,
  actions,
  emptyMessage = en.common.noData,
  emptyIcon,
  rowsPerPageOptions = [5, 10, 25, 50],
  dense = false,
}: DataTableProps<T>) {
  const theme = useTheme();
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(rowsPerPageOptions[1] || 10);
  const [exportAnchorEl, setExportAnchorEl] = useState<null | HTMLElement>(null);
  const [sortColumn, setSortColumn] = useState<keyof T | null>(null);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

  // Gestion du tri
  const handleSort = (columnKey: keyof T) => {
    const column = columns.find((col) => col.key === columnKey);
    if (!column?.sortable) return;

    if (sortColumn === columnKey) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(columnKey);
      setSortDirection('asc');
    }
  };

  // Filtrage des données
  const filteredData = useMemo(() => {
    if (!searchTerm) return data;

    return data.filter((row) =>
      columns.some((col) => {
        const value = row[col.key];
        return String(value).toLowerCase().includes(searchTerm.toLowerCase());
      })
    );
  }, [data, searchTerm, columns]);

  // Tri des données
  const sortedData = useMemo(() => {
    if (!sortColumn) return filteredData;

    return [...filteredData].sort((a, b) => {
      const aValue = a[sortColumn];
      const bValue = b[sortColumn];

      // Gestion des valeurs null/undefined
      if (aValue == null) return 1;
      if (bValue == null) return -1;

      // Tri numérique
      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
      }

      // Tri par date
      if (aValue instanceof Date && bValue instanceof Date) {
        return sortDirection === 'asc'
          ? aValue.getTime() - bValue.getTime()
          : bValue.getTime() - aValue.getTime();
      }

      // Tri par chaîne (essai de conversion de date ISO)
      const aStr = String(aValue);
      const bStr = String(bValue);

      // Tentative de détection de dates ISO
      const aDate = new Date(aStr);
      const bDate = new Date(bStr);
      if (
        !isNaN(aDate.getTime()) &&
        !isNaN(bDate.getTime()) &&
        (aStr.includes('-') || aStr.includes('/'))
      ) {
        return sortDirection === 'asc'
          ? aDate.getTime() - bDate.getTime()
          : bDate.getTime() - aDate.getTime();
      }

      // Tri alphabétique standard
      return sortDirection === 'asc'
        ? aStr.localeCompare(bStr, 'en', { sensitivity: 'base' })
        : bStr.localeCompare(aStr, 'en', { sensitivity: 'base' });
    });
  }, [filteredData, sortColumn, sortDirection]);

  // Pagination
  const paginatedData = useMemo(() => {
    const start = page * rowsPerPage;
    return sortedData.slice(start, start + rowsPerPage);
  }, [sortedData, page, rowsPerPage]);

  const handleExportCSV = () => {
    const exportData = prepareDataForExport(filteredData);
    downloadCSV(exportData, exportFilename);
    setExportAnchorEl(null);
  };

  const handleExportJSON = () => {
    const exportData = prepareDataForExport(filteredData);
    downloadJSON(exportData, exportFilename);
    setExportAnchorEl(null);
  };

  const showActions = Boolean(onEdit ?? onDelete ?? actions);

  return (
    <Box>
      {/* Barre d'outils */}
      <Paper
        elevation={0}
        sx={{
          p: 2.5,
          mb: 2,
          backgroundColor: theme.palette.background.paper,
          border: `1px solid ${theme.palette.divider}`,
        }}
      >
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
          {searchable && (
            <TextField
              size="small"
              placeholder={searchPlaceholder}
              value={searchTerm}
              onChange={(e) => {
                setSearchTerm(e.target.value);
                setPage(0);
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: theme.palette.text.secondary }} />
                  </InputAdornment>
                ),
              }}
              sx={{ flexGrow: 1, minWidth: 300 }}
            />
          )}

          {onRefresh && (
            <Tooltip title={en.dataTable.refresh}>
              <IconButton
                onClick={onRefresh}
                sx={{
                  color: theme.palette.text.secondary,
                  border: '1px solid',
                  borderColor: theme.palette.divider,
                  '&:hover': {
                    backgroundColor: theme.palette.action.hover,
                    color: theme.palette.primary.main,
                    borderColor: theme.palette.primary.main,
                  },
                }}
              >
                <RefreshIcon />
              </IconButton>
            </Tooltip>
          )}

          {exportable && (
            <>
              <Tooltip title={en.dataTable.export}>
                <IconButton
                  onClick={(e) => setExportAnchorEl(e.currentTarget)}
                  sx={{
                    color: theme.palette.text.secondary,
                    border: '1px solid',
                    borderColor: theme.palette.divider,
                    '&:hover': {
                      backgroundColor: theme.palette.action.hover,
                      color: theme.palette.primary.main,
                      borderColor: theme.palette.primary.main,
                    },
                  }}
                >
                  <DownloadIcon />
                </IconButton>
              </Tooltip>
              <Menu
                anchorEl={exportAnchorEl}
                open={Boolean(exportAnchorEl)}
                onClose={() => setExportAnchorEl(null)}
              >
                <MenuItem onClick={handleExportCSV}>
                  <DownloadIcon sx={{ mr: 1, fontSize: 20 }} />
                  {en.dataTable.exportCsv}
                </MenuItem>
                <MenuItem onClick={handleExportJSON}>
                  <DownloadIcon sx={{ mr: 1, fontSize: 20 }} />
                  {en.dataTable.exportJson}
                </MenuItem>
              </Menu>
            </>
          )}
        </Box>

        {searchTerm && (
          <Box
            sx={{
              mt: 2,
              p: 1.5,
              backgroundColor:
                theme.palette.mode === 'dark' ? 'rgba(74,144,226,0.1)' : 'rgba(74,144,226,0.08)',
              border: '1px solid',
              borderColor:
                theme.palette.mode === 'dark' ? 'rgba(74,144,226,0.2)' : 'rgba(74,144,226,0.3)',
              borderRadius: 2,
            }}
          >
            <Typography
              variant="body2"
              sx={{
                color: theme.palette.primary.main,
                fontSize: '0.875rem',
                fontWeight: 500,
              }}
            >
              {en.dataTable.searchResults(filteredData.length, searchTerm)}
            </Typography>
          </Box>
        )}
      </Paper>

      {/* Table */}
      <Paper
        elevation={0}
        sx={{
          backgroundColor: theme.palette.background.paper,
          border: `1px solid ${theme.palette.divider}`,
          overflow: 'hidden',
        }}
      >
        {isLoading ? (
          <SkeletonTable rows={rowsPerPage} columns={columns.length + (showActions ? 1 : 0)} />
        ) : (
          <>
            <TableContainer>
              <Table size={dense ? 'small' : 'medium'}>
                <TableHead>
                  <TableRow>
                    {columns.map((column) => (
                      <TableCell
                        key={String(column.key)}
                        align={column.align ?? 'left'}
                        sx={{
                          width: column.width,
                          cursor: column.sortable ? 'pointer' : 'default',
                          userSelect: 'none',
                          '&:hover': column.sortable
                            ? {
                                backgroundColor: theme.palette.action.hover,
                              }
                            : undefined,
                        }}
                        onClick={() => column.sortable && handleSort(column.key)}
                      >
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          {column.label}
                          {column.sortable &&
                            sortColumn === column.key &&
                            (sortDirection === 'asc' ? (
                              <ArrowUpwardIcon
                                sx={{ fontSize: 16, color: theme.palette.primary.main }}
                              />
                            ) : (
                              <ArrowDownwardIcon
                                sx={{ fontSize: 16, color: theme.palette.primary.main }}
                              />
                            ))}
                        </Box>
                      </TableCell>
                    ))}
                    {showActions && (
                      <TableCell align="center" sx={{ width: 120 }}>
                        {en.dataTable.actions}
                      </TableCell>
                    )}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedData.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={columns.length + (showActions ? 1 : 0)}
                        sx={{ textAlign: 'center', py: 8, borderBottom: 'none' }}
                      >
                        <Box>
                          {emptyIcon && <Box sx={{ mb: 2, opacity: 0.5 }}>{emptyIcon}</Box>}
                          <Typography
                            variant="h6"
                            sx={{ color: theme.palette.text.primary, mb: 1 }}
                          >
                            {emptyMessage}
                          </Typography>
                          {searchTerm && (
                            <Typography
                              variant="body2"
                              sx={{ color: theme.palette.text.secondary }}
                            >
                              Essayez de modifier votre recherche
                            </Typography>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  ) : (
                    paginatedData.map((row) => (
                      <TableRow key={row.id} hover>
                        {columns.map((column) => (
                          <TableCell key={String(column.key)} align={column.align ?? 'left'}>
                            {column.render
                              ? column.render(row[column.key], row)
                              : String(row[column.key])}
                          </TableCell>
                        ))}
                        {showActions && (
                          <TableCell align="center">
                            {actions ? (
                              actions(row)
                            ) : (
                              <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                                {onEdit && (
                                  <Tooltip title={en.dataTable.edit}>
                                    <IconButton
                                      size="small"
                                      onClick={() => onEdit(row)}
                                      sx={{
                                        color: theme.palette.text.secondary,
                                        border: '1px solid',
                                        borderColor: theme.palette.divider,
                                        '&:hover': {
                                          backgroundColor:
                                            theme.palette.mode === 'dark'
                                              ? 'rgba(74,144,226,0.1)'
                                              : 'rgba(74,144,226,0.08)',
                                          color: theme.palette.primary.main,
                                          borderColor: theme.palette.primary.main,
                                        },
                                      }}
                                    >
                                      <EditIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                )}
                                {onDelete && (
                                  <Tooltip title={en.dataTable.delete}>
                                    <IconButton
                                      size="small"
                                      onClick={() => onDelete(row)}
                                      sx={{
                                        color: theme.palette.text.secondary,
                                        border: '1px solid',
                                        borderColor: theme.palette.divider,
                                        '&:hover': {
                                          backgroundColor:
                                            theme.palette.mode === 'dark'
                                              ? 'rgba(239,68,68,0.1)'
                                              : 'rgba(239,68,68,0.08)',
                                          color: theme.palette.error.main,
                                          borderColor: theme.palette.error.main,
                                        },
                                      }}
                                    >
                                      <DeleteIcon fontSize="small" />
                                    </IconButton>
                                  </Tooltip>
                                )}
                              </Box>
                            )}
                          </TableCell>
                        )}
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Pagination */}
            <Box
              sx={{
                p: 2,
                borderTop: '1px solid',
                borderColor: theme.palette.divider,
                backgroundColor:
                  theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.02)',
              }}
            >
              <TablePagination
                component="div"
                count={sortedData.length}
                page={page}
                onPageChange={(_, newPage) => setPage(newPage)}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={(e) => {
                  setRowsPerPage(parseInt(e.target.value, 10));
                  setPage(0);
                }}
                labelRowsPerPage={en.dataTable.rowsPerPage}
                labelDisplayedRows={({ from, to, count }) =>
                  en.dataTable.displayedRows(from, to, count)
                }
                rowsPerPageOptions={rowsPerPageOptions}
                sx={{ color: theme.palette.text.secondary }}
              />
            </Box>
          </>
        )}
      </Paper>
    </Box>
  );
}
